<template>
  <div>
    <Head title="Dashboard" />

        <div class="mb-4 flex flex-wrap gap-4 items-center">
            <!-- Filter Department -->
            <div v-if="departments.length" class="flex gap-2 items-center">
                <label class="font-semibold db-filter-label">Filter Department:</label>
                <select v-model="filters.department_id" @change="updateFilter"
                    class="border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:text-white dark:border-gray-600">
                    <option value="">-- All Departments --</option>
                    <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                    {{ dept.name }}
                    </option>
                </select>
            </div>

            <!-- Filter Date Range -->
            <div class="flex gap-2 items-center">
                <label class="font-semibold db-filter-label">Filter Date:</label>
                
                <input type="date" v-model="filters.date_from" @change="updateFilter"
                    :disabled="filters.all_time"
                    class="border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:text-white dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed" />

                <span class="text-sm text-gray-500">to</span>

                <input type="date" v-model="filters.date_to" @change="updateFilter"
                    :disabled="filters.all_time"
                    class="border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:text-white dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed" />

                <!-- Toggle All Time -->
                <button
                    type="button"
                    @click="filters.all_time = !filters.all_time; handleAllTimeChange()"
                    :class="[
                        'all-time-toggle',
                        filters.all_time ? 'all-time-toggle--on' : 'all-time-toggle--off'
                    ]"
                >
                    <span class="all-time-track">
                        <span class="all-time-knob"></span>
                    </span>
                    <span class="all-time-label">All Time</span>
                </button>
            </div>
        </div>

        <!-- Card Kuota -->
        <div class="flex flex-col sm:flex-row gap-4 mt-5 mb-6" v-if="['admin','super_admin'].includes(role_slug) || role_slug === 'customer' || role_slug === 'general' || (role_slug === 'supervisor' && user_department_id === 3) || (role_slug === 'agency' && user_department_id === 3)">
            <!-- Card Kuota -->
            <div class="w-full sm:w-1/2 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between" v-if="customer_ticket_info">
                <div class="text-s mb-1">Ticket ke BNC Hari Ini</div>
                <div class="text-3xl font-bold" :class="customer_ticket_info.limit_reached ? 'text-red-600' : 'text-blue-600'">
                    {{ customer_ticket_info.total_dept3_today }}/10
                </div>
                <div class="text-xs mt-1" :class="customer_ticket_info.limit_reached ? 'text-red-500' : 'text-blue-500'">
                    {{ customer_ticket_info.limit_reached ? 'Kuota penuh' : customer_ticket_info.remaining_slots + ' slot tersisa' }}
                </div>
            </div>

            <!-- Card Sedang Dikerjakan -->
            <div class="w-full sm:w-1/2 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">Sedang Dikerjakan Hari Ini</div>
                <div class="text-3xl font-bold text-orange-600">
                    {{ new Intl.NumberFormat('id-ID').format(today_processing_tickets || 0) }}
                </div>
            </div>
        </div>

        <div class="grid grid-cols-6 gap-4">
            <!-- Total Tickets -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">Total Tickets</div>
                <div class="text-3xl font-bold">{{ new Intl.NumberFormat('id-ID').format(total_tickets || 0) }}</div>
            </div>

            <!-- Pending Tickets -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">Pending Tickets</div>
                <div class="text-3xl font-bold text-yellow-600">
                    {{ new Intl.NumberFormat('id-ID').format(pending_tickets || 0) }}
                </div>
                <div class="text-xs mt-1">
                    {{ calculatePercentage(pending_tickets) }}
                </div>
            </div>

            <!-- Passed Tickets -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">Passed</div>
                <div class="text-3xl font-bold text-green-600">
                    {{ new Intl.NumberFormat('id-ID').format(passed_tickets || 0) }}
                </div>
                <div class="text-xs mt-1">
                    {{ calculatePercentage(passed_tickets) }}
                </div>
            </div>

            <!-- Rejected Tickets -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">Rejected</div>
                <div class="text-3xl font-bold text-red-600">
                    {{ new Intl.NumberFormat('id-ID').format(rejected_tickets || 0) }}
                </div>
                <div class="text-xs mt-1">
                    {{ calculatePercentage(rejected_tickets) }}
                </div>
            </div>

            <!-- Brief Crafted Tickets -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">Brief Crafted</div>
                <div class="text-3xl font-bold text-blue-600">
                    {{ new Intl.NumberFormat('id-ID').format(brief_crafted_tickets || 0) }}
                </div>
                <div class="text-xs mt-1">
                    {{ calculatePercentage(brief_crafted_tickets) }}
                </div>
            </div>

            <!-- Assigned Tickets -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">Assigned Tickets</div>
                <div class="text-3xl font-bold text-blue-600">
                    {{ new Intl.NumberFormat('id-ID').format(assigned_tickets || 0) }}
                </div>
                <div class="text-xs mt-1">
                    {{ calculatePercentage(assigned_tickets) }}
                </div>
            </div>

            <!-- Processing Tickets -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">On Process Tickets</div>
                <div class="text-3xl font-bold text-indigo-600">
                    {{ new Intl.NumberFormat('id-ID').format(processing_tickets || 0) }}
                </div>
                <div class="text-xs mt-1">
                    {{ calculatePercentage(processing_tickets) }}
                </div>
            </div>

            <!-- Revisi Assets Tickets -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">Revisi Assets</div>
                <div class="text-3xl font-bold text-red-600">
                    {{ new Intl.NumberFormat('id-ID').format(revisi_assets_tickets || 0) }}
                </div>
                <div class="text-xs mt-1">
                    {{ calculatePercentage(revisi_assets_tickets) }}
                </div>
            </div>

            <!-- Revisi Brief Tickets -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">Revisi Brief</div>
                <div class="text-3xl font-bold text-pink-600">
                    {{ new Intl.NumberFormat('id-ID').format(revisi_brief_tickets || 0) }}
                </div>
                <div class="text-xs mt-1">
                    {{ calculatePercentage(revisi_brief_tickets) }}
                </div>
            </div>

            <!-- Internal Review Tickets -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">Internal Review</div>
                <div class="text-3xl font-bold text-indigo-600">
                    {{ new Intl.NumberFormat('id-ID').format(internal_review_tickets || 0) }}
                </div>
                <div class="text-xs mt-1">
                    {{ calculatePercentage(internal_review_tickets) }}
                </div>
            </div>

            <!-- External Review Tickets -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">External Review</div>
                <div class="text-3xl font-bold text-teal-600">
                    {{ new Intl.NumberFormat('id-ID').format(external_review_tickets || 0) }}
                </div>
                <div class="text-xs mt-1">
                    {{ calculatePercentage(external_review_tickets) }}
                </div>
            </div>

            <!-- Closed Tickets -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">Closed Tickets</div>
                <div class="text-3xl font-bold text-green-600">
                    {{ new Intl.NumberFormat('id-ID').format(closed_tickets || 0) }}
                </div>
                <div class="text-xs mt-1">
                    {{ calculatePercentage(closed_tickets) }}
                </div>
            </div>

            <!-- Total Output -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                <div class="text-s mb-1">Total Output BNC</div>
                <div class="text-3xl font-bold text-green-600">
                    {{ new Intl.NumberFormat('id-ID').format(total_output_bram || 0) }}
                </div>
            </div>

            <!-- Total Late -->
            <div class="col-span-6 sm:col-span-3 md:col-span-1 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between" v-if="['admin','super_admin'].includes(role_slug)">
                <div class="text-s mb-1">Total Late Tickets</div>
                <div class="text-3xl font-bold text-red-600">
                    {{ new Intl.NumberFormat('id-ID').format(late_closed_tickets || 0) }}
                </div>
                <div class="text-xs mt-1">
                    {{ calculatePercentage(late_closed_tickets) }}
                </div>
            </div>

        </div>

        <!-- TABLE FOR DEADLINE-->
        <div class="mt-10" v-if="!['customer', 'general'].includes(role_slug)">

            <h3 class="text-xl font-semibold mb-4 db-filter-label">Tiket-tiket Deadline</h3>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

                <Link href="/dashboard/tickets?type=late-last-week" class="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between mb-6">
                    <div class="text-s mb-1">Late Last Week</div>
                    <div class="text-3xl font-bold text-indigo-600">
                        {{ new Intl.NumberFormat('id-ID').format(late_last_week || 0) }}
                    </div>
                </Link>

                <Link href="/dashboard/tickets?type=late-this-week" class="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between mb-6">
                    <div class="text-s mb-1">Late This Week</div>
                    <div class="text-3xl font-bold" :class="late_this_week > late_last_week ? 'text-red-600' : 'text-green-600'">
                        {{ new Intl.NumberFormat('id-ID').format(late_this_week || 0) }}
                    </div>

                    <div class="text-xs mt-1 flex items-center gap-1 text-gray-600">
                        <template v-if="lateTrend.direction === 'up'">
                            <span class="text-red-500">🔺</span> {{ lateTrend.change }}% naik dari minggu lalu
                        </template>
                        <template v-else-if="lateTrend.direction === 'down'">
                            <span class="text-green-500">🔻</span> {{ lateTrend.change }}% turun dari minggu lalu
                        </template>
                        <template v-else>
                            Tidak ada perubahan dari minggu lalu
                        </template>
                    </div>
                </Link>
            </div>

            <div class="overflow-auto rounded-xl bg-white border border-gray-100 shadow-sm" v-if="!['customer', 'general'].includes(role_slug)">
                <table class="min-w-full text-sm text-left table-fixed ticket_list">
                <thead>
                    <tr class="text-left font-bold">
                    <th class="w-1/3">Today</th>
                    <th class="w-1/3">Tomorrow</th>
                    <th class="w-1/3">Day After Tomorrow</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                    <!-- Hari Ini -->
                    <td class="px-5 py-3 border-r border-gray-100 align-top">
                        <ul class="space-y-1.5">
                        <li
                            v-for="ticket in tickets_near_deadline.today"
                            :key="'today-' + ticket.id"
                            class="flex items-start gap-1"
                        >
                            <a :href="route('tickets.edit', ticket.id)" class="text-rose-500 hover:text-rose-700 font-medium shrink-0">
                                #{{ ticket.uid }}
                            </a>
                            <span class="text-gray-500"> — {{ ticket.subject }}</span>
                        </li>
                        </ul>
                    </td>

                    <!-- Besok -->
                    <td class="px-5 py-3 border-r border-gray-100 align-top">
                        <ul class="space-y-1.5">
                        <li
                            v-for="ticket in tickets_near_deadline.tomorrow"
                            :key="'tomorrow-' + ticket.id"
                            class="flex items-start gap-1"
                        >
                            <a :href="route('tickets.edit', ticket.id)" class="text-amber-500 hover:text-amber-700 font-medium shrink-0">
                                #{{ ticket.uid }}
                            </a>
                            <span class="text-gray-500"> — {{ ticket.subject }}</span>
                        </li>
                        </ul>
                    </td>

                    <!-- Lusa -->
                    <td class="px-5 py-3 align-top">
                        <ul class="space-y-1.5">
                        <li
                            v-for="ticket in tickets_near_deadline.day_after"
                            :key="'dayafter-' + ticket.id"
                            class="flex items-start gap-1"
                        >
                            <a :href="route('tickets.edit', ticket.id)" class="text-yellow-600 hover:text-yellow-800 font-medium shrink-0">
                                #{{ ticket.uid }}
                            </a>
                            <span class="text-gray-500"> — {{ ticket.subject }}</span>
                        </li>
                        </ul>
                    </td>
                    </tr>
                </tbody>
                </table>
            </div>
        </div>

        <div class="mt-8" v-if="!['customer', 'general', 'agency'].includes(role_slug)">
            <BarChart :data="traffic_per_day" />
        </div>

        <!-- Chart: Donut kiri-kanan -->
        <div class="mt-8" v-if="!['customer', 'general', 'agency'].includes(role_slug)">
            <div class="flex flex-col md:flex-row gap-4">
                <!-- Tiket per Kategori -->
                <div class="flex-1 bg-white p-4 rounded-2xl border shadow">
                    <h3 class="text-md font-semibold text-center mb-2">Tiket per Kategori</h3>
                    <DonutChart :ticket_by_category_month="ticket_by_category_month" />
                </div>

                <!-- Tiket per Creator -->
                <div class="flex-1 bg-white p-4 rounded-2xl border shadow">
                    <h3 class="text-md font-semibold text-center mb-2">Tiket per Creator</h3>
                    <CreatorDonutChart :ticket_by_creator_month="ticket_by_creator_month" />
                </div>
            </div>
        </div>

  </div>
</template>

<script setup>
    import { Head, router, Link } from '@inertiajs/vue3'
    import Layout from '@/Shared/Layout'
    import { ref, computed, watch } from 'vue'
    import BarChart from '@/Shared/BarChart'
    import DonutChart from '@/Shared/DonutChart'
    import CreatorDonutChart from '@/Shared/CreatorDonutChart'

    defineOptions({
        layout: Layout,
    })

    const props = defineProps({
        total_tickets: Number,
        pending_tickets: Number,
        brief_crafted_tickets: Number,
        passed_tickets: Number,
        rejected_tickets: Number,
        assigned_tickets: Number,
        processing_tickets: Number,
        revisi_assets_tickets: Number,
        revisi_brief_tickets: Number,
        internal_review_tickets: Number,
        external_review_tickets: Number,
        closed_tickets: Number,
        total_output_bram: Number,
        late_closed_tickets: Number,
        filters: {
            type: Object,
            default: () => ({})
        },
        departments: {
            type: Array,
            default: () => []
        },
        tickets_near_deadline: {
            type: Object,
            default: () => ({
                today: [],
                tomorrow: [],
                day_after: [],
            }),
        },
        late_this_week: Number,
        late_last_week: Number,
        role_slug: String,
        traffic_per_day: {
            type: Array,
            default: () => [],
        },
        ticket_by_category_month: {
            type: Array,
            default: () => [],
        },
        ticket_by_creator_month: {
            type: Array,
            default: () => [],
        },
        customer_ticket_info: Object,
        today_processing_tickets: Number,
        user_department_id: Number,
    })

    const getTodayDate = () => {
        const today = new Date()
        return today.toISOString().split('T')[0]
    }

    const filters = ref({
        department_id: props.filters.department_id || '',
        date_from: props.filters.date_from || getTodayDate(),
        date_to: props.filters.date_to || getTodayDate(),
        all_time: props.filters.all_time || false,
    })

    const updateFilter = () => {
        router.get(route('dashboard'), {
            department_id: filters.value.department_id,
            date_from: filters.value.all_time ? '' : filters.value.date_from,
            date_to: filters.value.all_time ? '' : filters.value.date_to,
            all_time: filters.value.all_time,
        }, {
            preserveState: true,
            replace: true
        })
    }

    const handleAllTimeChange = () => {
        if (filters.value.all_time) {
            // Jika all_time dicentang, kosongkan tanggal dan update
            updateFilter()
        } else {
            // Jika all_time dilepas, reset ke hari ini dan langsung update
            filters.value.date_from = getTodayDate()
            filters.value.date_to = getTodayDate()
            // Langsung update tanpa menunggu watch
            updateFilter()
        }
    }

    watch(
        () => [filters.value.date_from, filters.value.date_to],
        ([from, to]) => {
            if (!filters.value.all_time && (from || to)) {
                updateFilter()
            }
        }
    )

    const calculatePercentage = (value) => {
        if (!props.total_tickets || props.total_tickets === 0) return '0%'
        const percent = (value / props.total_tickets) * 100
        return `${percent.toFixed(1)}% dari total`
    }

    const lateTrend = computed(() => {
        const current = props.late_this_week
        const prev = props.late_last_week
        if (prev === 0) return { change: 0, direction: null }

        const diff = current - prev
        const percent = (diff / prev) * 100

        return {
            change: percent.toFixed(1),
            direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'same'
        }
    })

</script>

<style scoped>
.all-time-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 12px 5px 6px;
    border-radius: 999px;
    border: 1.5px solid;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s, color 0.2s;
    outline: none;
    user-select: none;
}

/* OFF state */
.all-time-toggle--off {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #6b7280;
}
.all-time-toggle--off:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
    color: #374151;
}

/* ON state */
.all-time-toggle--on {
    background: #f0fdf4;
    border-color: #15803d;
    color: #15803d;
}
.all-time-toggle--on:hover {
    background: #dcfce7;
}

/* Track (the sliding pill) */
.all-time-track {
    position: relative;
    width: 32px;
    height: 18px;
    border-radius: 999px;
    transition: background 0.2s;
    flex-shrink: 0;
}
.all-time-toggle--off .all-time-track { background: #d1d5db; }
.all-time-toggle--on  .all-time-track { background: #15803d; }

/* Knob */
.all-time-knob {
    position: absolute;
    top: 2px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    transition: left 0.2s cubic-bezier(.4,0,.2,1);
}
.all-time-toggle--off .all-time-knob { left: 2px; }
.all-time-toggle--on  .all-time-knob { left: 16px; }

.all-time-label { line-height: 1; }

/* Dark mode */
:global(.dark) .all-time-toggle--off {
    background: #1f2937;
    border-color: #4b5563;
    color: #9ca3af;
}
:global(.dark) .all-time-toggle--off:hover {
    background: #374151;
    border-color: #6b7280;
    color: #d1d5db;
}
:global(.dark) .all-time-toggle--on {
    background: rgba(21,128,61,0.15);
    border-color: #4ade80;
    color: #4ade80;
}
:global(.dark) .all-time-toggle--on .all-time-track { background: #15803d; }
</style>

<style>
.dark .db-filter-label {
  color: #ffffff;
}
</style>